import { Component, OnInit } from '@angular/core';
import { ColDef, GridOptions, GridApi } from "ag-grid-community";
import { dateComparator } from '../../../../common/components/data-table/comparators/data-table.comparators';
import { SubHeader } from '../../../../core/models/sub-header.model';
import { SubHeaderService } from '../../../../core/services/sub-header.service';
import { Subscription } from 'rxjs';
import { tap } from "rxjs/operators";
import { RemindersService } from '../../../../admin/services/reminders.service';
import { DatePipe } from '@angular/common';
import { DateService } from '../../../../common/services/date.service';
import { GridFormatterService } from '../../../../common/services/grid-formatter.service';
import { FinancialAssuranceService } from '../../../../common/services/financial-assurance.service';
import { InsuranceSearchCriteria } from '../../../../admin/models/admin-reports.model';
import { ExpiringInsuranceDetailed } from '../../../../common/models/financialAssurance.model';
import { OperationType } from "../../../../common/models/organization.model";


@Component({
    selector: 'app-insurance-date-listing-report',
    templateUrl: './insurance-date-listing-report.component.html',
    styleUrls: ['./insurance-date-listing-report.component.scss'],
    standalone: false
})

export class InsuranceDateListingReportComponent implements OnInit {
    reportData: ExpiringInsuranceDetailed[];
    subHeader: SubHeader = new SubHeader({
        title: "Insurance Date Listing",
        buttons: []
    });
	gridOptions: GridOptions;
	gridApi?: GridApi;
    currentDate: Date = new Date();   
    toDate: Date | null;
    fromDate: Date | null;
    subscriptions: Subscription = new Subscription();

    constructor (public subHeaderService: SubHeaderService,
        private datePipe: DatePipe,
        private readonly dateService: DateService,
        private readonly formatService: GridFormatterService,
        public remindersService: RemindersService,
		public faService: FinancialAssuranceService) {
    }

    ngOnInit() {
        this.subHeaderService.updateSubHeader(this.subHeader);
        this.fromDate = this.dateService.generateDateWithOffset(0, -1, 0);
        this.toDate = this.currentDate;
        let coldefs = this.createColumnDefinitions();
        this.gridOptions = this.formatService.BuildDefaultGridOptionsWithColumns(coldefs, false, true, true, true);
        this.gridOptions.suppressColumnVirtualisation = true;
        this.gridOptions.onGridReady = (params) => {
			this.gridApi = params.api;
			this.gridApi.sizeColumnsToFit();
        };
        this.gridOptions.headerHeight = 50;
        this.generateReport();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    generateReport() {
        if (this.gridApi) {
            this.gridApi.setGridOption("loading", true);
        }
        const criteria = new InsuranceSearchCriteria();
        criteria.expirationFromDate = this.fromDate;
        criteria.expirationToDate = this.toDate;
        this.subscriptions.add(this.faService.getInsuranceOwnersExpiringSoon(criteria).pipe(          
            ).subscribe({
                next: result => {
                    if (this.gridApi != null) {
                            this.gridApi.setGridOption("loading", false);
                    }
                    result.forEach(policy => {
                        if (policy.organizationId === policy.legacyOrganizationId) {
                            policy.organizationId = "";
                        }
                        policy.type = policy.policy.type ?? "";
                        policy.number = policy.policy.policyNumber ?? "";
                    });
                    this.reportData = result;
        }, 
            error: (fail) => {
                console.error(fail);
                if (this.gridApi != null) {
                    this.gridApi.setGridOption("loading", false);
                }
        }}
        ));
    };

    public createColumnDefinitions() {
        return [
            {
                headerClass: "wrapped",
                headerName: "RICS Owner Name",
                headerTooltip: "RICS Owner Name",
                field: "organizationName",
                tooltipField: "organizationName",
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                minWidth: 135,
            },
            {
                headerClass: "wrapped",
                headerName: "RICS Owner Number",
                headerTooltip: "RICS Owner Number",
                field: "organizationId",
                tooltipField: "organizationId",
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                minWidth: 135,
            },
            {
                headerClass: "wrapped",
                headerName: "RBDMS Owner Number",
                headerTooltip: "RBDMS Owner Number",
                field: "legacyOrganizationId",
                tooltipField: "legacyOrganizationId",
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                minWidth: 140,
            },
             {
                headerClass: "wrapped",
                headerName: "Owner Type",
                headerTooltip: "Owner Type",
                tooltipValueGetter: this.formatOwnerType(),
                valueGetter: this.formatOwnerType(),
                minWidth: 130
            },
            {
                headerClass: "wrapped",
                headerName: "Insurance Type",
                headerTooltip: "Insurance Type",
                field: "type",
                tooltipField: "type",
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                minWidth: 140,
            },
            {
                headerClass: "wrapped",
                headerName: "Policy Number",
                headerTooltip: "Policy Number",
                field: "number",
                tooltipField: "number",
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                minWidth: 130,
            },
            {
                headerClass: "wrapped",
                headerName: "Expiration Date",
                headerTooltip: "Expiration Date",
                field: "expirationDate",
                tooltipValueGetter: this.formatService.formatDate("expirationDate"),
                valueGetter: this.formatService.formatDate("expirationDate"),
                comparator: dateComparator,
                filter: "agDateColumnFilter",
                filterParams: this.formatService.defaultDateFilterParams,
                minWidth: 140,
            },
            {
                headerClass: "wrapped",
                headerName: "45 Day Letter Date Mailed",
                headerTooltip: "45 Day Letter Date Mailed",
                field: "fortyFiveDayDayReminderSent",
                tooltipValueGetter: this.formatService.formatDate("fortyFiveDayDayReminderSent", "MM/dd/yyyy", "N/A"),
                valueGetter: this.formatService.formatDate("fortyFiveDayDayReminderSent", "MM/dd/yyyy", "N/A"),
                comparator: dateComparator,
                filter: "agDateColumnFilter",
                filterParams: this.formatService.defaultDateFilterParams,
                minWidth: 150,
            },
            {
                headerClass: "wrapped",
                headerName: "45 Day Letter Date Received",
                headerTooltip: "45 Day Letter Date Received",
                field: "fortyFiveDayDayReminderReceived",
                tooltipValueGetter: this.formatService.formatDate("fortyFiveDayDayReminderReceived", "MM/dd/yyyy", "N/A"),
                valueGetter: this.formatService.formatDate("fortyFiveDayDayReminderReceived", "MM/dd/yyyy", "N/A"),
                comparator: dateComparator,
                filter: "agDateColumnFilter",
                filterParams: this.formatService.defaultDateFilterParams,
                minWidth: 150,
            }
        ] as ColDef[];
    }

    get invalidDateCombination(): boolean {
        let invalid: boolean = false;
        if (this.toDate == null || this.fromDate == null) {
            invalid = true;
        } else if (this.toDate != null && this.fromDate != null) {
            invalid = this.toDate < this.fromDate;
        }
        return invalid;
    }

    formatOwnerType(): any {
        return (obj) => {
            let returnValue: string = "";
            if (obj.data != null && obj.data["ownerType"] != null) {
                let isBrineHaulerFunc = this.formatService.flagEnumFormatter("ownerType", OperationType, "BrineHauler", true);
                let isOperatorFunc = (this.formatService.flagEnumFormatter("ownerType", OperationType, "Operator", true));
                let isInjectionOperatorFunc =  (this.formatService.flagEnumFormatter("ownerType", OperationType, "InjectionOperator", true));
                if (isBrineHaulerFunc(obj) == true) {
                    returnValue = "Brine Hauler";
                }
                if (isOperatorFunc(obj) == true || isInjectionOperatorFunc(obj) == true) {
                    if (returnValue.length > 0) returnValue += ", ";
                    returnValue += "Well Owner";
                }               
            }
            if (returnValue.length === 0) returnValue = "N/A";
            return returnValue;
        }
    }

    fromDateChanged(model: Date): void {
        this.fromDate = model;
    }

    toDateChanged(model: Date): void {
        this.toDate = model;
    }

    downloadReport() {

      const criteria = new InsuranceSearchCriteria();
      criteria.expirationFromDate = this.fromDate;
      criteria.expirationFromDate = this.toDate;
      
      const fromDate = this.datePipe.transform(this.fromDate, "yyyy-MM-dd");
      const toDate = this.datePipe.transform(this.toDate, "yyyy-MM-dd");
      const fileName = `InsuranceDateListing_${fromDate}_${toDate}`;
      const sheetName = "Insurance Date Listing";
      
      this.formatService.exportDataToExcel(fileName, sheetName, this.gridApi);
  }

    onGridPageChanged(event: any) {
        this.gridApi.autoSizeAllColumns();
    }

    resizeColumns(): void {
        if (this.gridApi) {
            this.gridApi.sizeColumnsToFit();
        }
    }
}
